import React from 'react'
import { Heading, Text, Box, useColorModeValue, Flex, Spinner, VStack } from '@chakra-ui/react'
import { motion } from 'framer-motion'

const MotionBox = motion(Box)
const MotionFlex = motion(Flex)

const Loading: React.FC<{ message: string; fullScreen?: boolean }> = ({ message, fullScreen = false }) => {
  const overlayBg = useColorModeValue('rgba(255, 255, 255, 0.94)', 'rgba(11, 15, 25, 0.96)')
  const textColor = useColorModeValue('gray.600', 'gray.400')
  const glowColor = useColorModeValue('rgba(14, 165, 233, 0.25)', 'rgba(16, 185, 129, 0.32)')

  return (
    <Flex
      position={fullScreen ? 'fixed' : 'relative'}
      top={0}
      left={0}
      right={0}
      bottom={0}
      w={fullScreen ? '100vw' : '100%'}
      h={fullScreen ? '100vh' : '100%'}
      minH={fullScreen ? '100vh' : '50vh'}
      zIndex={fullScreen ? 99999 : 1}
      alignItems="center"
      justifyContent="center"
      flexDir="column"
      bg={fullScreen ? overlayBg : 'transparent'}
      backdropFilter={fullScreen ? 'blur(20px)' : 'none'}
    >
      <MotionFlex
        initial={{ opacity: 0, scale: 0.92 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        flexDir="column"
        alignItems="center"
        justifyContent="center"
        pos="relative"
      >
        {/* Floating Ambient Aura Spinner */}
        <Box pos="relative" w="100px" h="100px" display="flex" alignItems="center" justifyContent="center">
          {/* Subtle Ambient Radial Ultrasound Glow */}
          <MotionBox
            pos="absolute"
            w="90px"
            h="90px"
            borderRadius="full"
            bg={glowColor}
            filter="blur(20px)"
            animate={{ opacity: [0.35, 0.75, 0.35], scale: [0.9, 1.15, 0.9] }}
            transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}
          />

          {/* Smooth Soft Scanning Ripple Ring */}
          <MotionBox
            pos="absolute"
            w="90px"
            h="90px"
            borderRadius="full"
            border="2px solid"
            borderColor={useColorModeValue('rgba(14, 165, 233, 0.4)', 'rgba(16, 185, 129, 0.4)')}
            animate={{ scale: [0.85, 1.25, 0.85], opacity: [0.6, 0, 0.6] }}
            transition={{ repeat: Infinity, duration: 2.0, ease: 'easeInOut' }}
          />

          <Spinner
            thickness="3px"
            speed="0.8s"
            emptyColor="transparent"
            color="#0ea5e9"
            size="xl"
          />
        </Box>

        {/* Brand & Loading Label */}
        <VStack spacing={2} mt={6} textAlign="center">
          <Heading
            size="md"
            fontWeight="500"
            letterSpacing="1.2px"
            color={textColor}
          >
            XN
            <Text
              as="span"
              aria-label="A"
              fontWeight="600"
              bgGradient="linear(to-r, #38bdf8, #0ea5e9, #10b981)"
              bgClip="text"
              display="inline-block"
              filter="drop-shadow(0 0 10px rgba(56, 189, 248, 0.6))"
              mx="0.5px"
            >
              Λ
            </Text>
            V Studio
          </Heading>

          <MotionBox
            animate={{ opacity: [0.55, 1, 0.55] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
          >
            <Text
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              letterSpacing="0.2px"
            >
              {message}
            </Text>
          </MotionBox>
        </VStack>
      </MotionFlex>
    </Flex>
  )
}

export default Loading

